/**
 * UserMenu — avatar button in the TopNav that opens a small popover with
 * profile shortcuts and Sign out. Closes on outside click or Escape.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Settings as SettingsIcon, UserCog } from 'lucide-react';
import { useAuth } from '@/src/modules/auth/useAuth';
import { avatarSrc } from '@/src/infrastructure/lib/avatar';
import { cn } from '@/src/infrastructure/lib/utils';

export function UserMenu() {
  const { user, profile, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Prefer the local DB profile over the Firebase user — Firebase's `photoURL`
  // and `displayName` are baked into the ID token and only refresh when the
  // token is reissued, so profile edits would otherwise appear stale here.
  const displayName =
    profile?.name ||
    user?.displayName ||
    user?.email?.split('@')[0] ||
    'Admin';
  const email = profile?.email ?? user?.email ?? '';
  const initial = displayName.charAt(0).toUpperCase();
  const photoUrl = avatarSrc(profile?.avatar ?? user?.photoURL ?? null, profile?.updatedAt);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const onSignOut = async () => {
    setOpen(false);
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`User menu for ${displayName}`}
        className={cn(
          'flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-border-subtle hover:bg-white/5 transition-colors',
          open && 'bg-white/5',
        )}
      >
        <span className="w-8 h-8 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary font-bold overflow-hidden">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={displayName}
              className="w-full h-full object-cover"
              // If the hosted image fails (CDN delay, broken URL), drop back
              // to the initial badge instead of showing a broken-image icon.
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <span className="text-sm">{initial}</span>
          )}
        </span>
        <span className="hidden sm:block text-sm text-on-surface max-w-[140px] truncate">
          {displayName}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 rounded-2xl glass-panel p-2 z-50"
        >
          <div className="px-3 py-3 border-b border-border-subtle">
            <p className="text-sm font-semibold text-on-surface truncate">{displayName}</p>
            <p className="text-xs text-on-surface-variant truncate">{email}</p>
            <p className="text-[10px] uppercase tracking-wider text-outline mt-1">
              {isAdmin ? 'System Admin' : 'User'}
            </p>
          </div>
          <MenuItem icon={<UserCog className="w-4 h-4" />} onClick={() => go('/settings?section=profile')}>
            Profile
          </MenuItem>
          <MenuItem icon={<SettingsIcon className="w-4 h-4" />} onClick={() => go('/settings')}>
            Settings
          </MenuItem>
          <div className="my-1 h-px bg-border-subtle" />
          <MenuItem
            icon={<LogOut className="w-4 h-4" />}
            onClick={onSignOut}
            tone="danger"
          >
            Sign out
          </MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  children,
  onClick,
  tone,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
  tone?: 'danger';
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
        tone === 'danger'
          ? 'text-rose-300 hover:bg-rose-500/10'
          : 'text-on-surface hover:bg-white/5',
      )}
    >
      {icon}
      {children}
    </button>
  );
}
